/**
 * EmptyState — zero-data placeholder matching the brandbook's `.empty` layout.
 *
 * Renders an icon in a rounded art box, a heading, a muted caption, and an
 * optional primary action button. Both `actionLabel` and `onAction` must be
 * supplied for the button to appear; providing only one of the two silently
 * omits it (avoids an unclickable or unlabelled CTA).
 *
 * Usage:
 *   <EmptyState icon="folder" title="No projects" caption="Create one to begin." />
 *   <EmptyState icon="bell" title="All caught up" caption="Check back later."
 *               actionLabel="Refresh" onAction={refetch} />
 */

import { Icon } from "@/components/Icon";
import type { IconName } from "@/components/Icon";
import { Button } from "@/components/Button";

export type EmptyStateProps = {
  icon: IconName;
  title: string;
  caption: string;
  actionLabel?: string;
  onAction?: () => void;
};

export function EmptyState({
  icon,
  title,
  caption,
  actionLabel,
  onAction,
}: EmptyStateProps) {
  return (
    <div className="empty">
      <div className="e-art" aria-hidden="true">
        <Icon name={icon} size={22} />
      </div>
      <h3 className="e-title">{title}</h3>
      <p className="e-caption t-sm">{caption}</p>
      {actionLabel && onAction ? (
        <div className="e-action">
          <Button variant="primary" onClick={onAction}>
            {actionLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
